import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import {
  agentKeys,
  apiClient,
} from "@alaffia-technology-solutions/client-sdk-react-query";
import {
  Button,
  deprecatedToast,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
} from "@alaffia-technology-solutions/ds";
import { TaskArtifactOutput } from "@alaffia-technology-solutions/schema";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const artifactSchema = z.union([
  TaskArtifactOutput.extend({ title: z.string() }),
  z
    .object({
      task_artifact_output: TaskArtifactOutput,
      title: z.string(),
    })
    .transform(({ task_artifact_output, title }) => ({
      title,
      ...task_artifact_output,
    })),
]);

const artifactsSchema = z
  .union([artifactSchema, z.array(artifactSchema)])
  .transform((value) => (Array.isArray(value) ? value : [value]));

const createAgentDemoFormSchema = z.object({
  agentName: z.string().min(1, "Agent name is required"),
  file: z
    .instanceof(File, { message: "File is required" })
    .refine((file) => file.name.toLowerCase().endsWith(".json"), {
      message: "File must be a JSON file",
    }),
});

interface CreateDemoAgentFormProps {
  caseId: string;
}

export const CreateDemoAgentForm = ({ caseId }: CreateDemoAgentFormProps) => {
  const form = useForm<z.infer<typeof createAgentDemoFormSchema>>({
    defaultValues: {
      agentName: "",
      file: undefined,
    },
    resolver: zodResolver(createAgentDemoFormSchema),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const { mutateAsync: createAgentDemo } =
    apiClient.cases.createAgentDemo.useMutation({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: agentKeys.lists(),
        }),
    });

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      form.reset();
      deprecatedToast({ title: "Agent Demo created" });
    }
  }, [form, form.formState.isSubmitSuccessful, deprecatedToast]);
  return (
    <Form {...form}>
      <form
        className="af-flex af-w-[400px] af-flex-col af-gap-4"
        onSubmit={form.handleSubmit(async ({ agentName, file }) => {
          const fileText = await file.text();

          let artifacts;

          try {
            artifacts = artifactsSchema.parse(JSON.parse(fileText));
          } catch {
            form.setError("file", {
              message: "Wrong file format",
            });

            return;
          }

          const documentIds = Array.from(
            new Set(
              artifacts.flatMap((artifact) => [
                ...artifact.citations.map((citation) => citation.file_id),
                ...(artifact.inline_citations ?? []).flatMap((citation) =>
                  citation.annotations.map((annotation) => annotation.file.id),
                ),
              ]),
            ),
          );

          await createAgentDemo({
            input: {
              artifacts: artifacts.map((artifact) => ({
                title: artifact.title,
                markdown: artifact.markdown,
                citations: artifact.citations.map(
                  ({ file_id, page_index, citation_key }) => ({
                    document_id: file_id,
                    page_index,
                    citation_key,
                  }),
                ),
                inline_citations: artifact.inline_citations,
              })),
              caseId,
              documentIds,
              taskDisplayLabel: agentName,
            },
          });
        })}
      >
        <FormField
          control={form.control}
          name="agentName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Agent Name</FormLabel>
              <FormControl>
                <Input placeholder="Agent Name" {...field} />
              </FormControl>
              <p className="af-min-h-5 af-text-sm af-font-medium af-leading-5 af-text-destructive">
                {fieldState.error?.message}
              </p>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="file"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>File</FormLabel>
              <FormControl>
                <Input
                  accept=".json,application/json"
                  name={field.name}
                  onBlur={field.onBlur}
                  placeholder="File"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];

                    if (file) {
                      field.onChange(file);
                    }
                  }}
                  ref={(element) => {
                    field.ref(element);
                    fileInputRef.current = element;
                  }}
                />
              </FormControl>
              <p className="af-min-h-5 af-text-sm af-font-medium af-leading-5 af-text-destructive">
                {fieldState.error?.message}
              </p>
            </FormItem>
          )}
        />
        <Button type="submit">Submit</Button>
      </form>
    </Form>
  );
};
